/**
 *  @class
 *  @function AdultGate
 *  The Adults 18+ consent screen (sections/adult-gate.liquid). Shown once per browser session: consent is kept in
 *  sessionStorage under data-storage-key, which the layout's head check also reads to release the screen before
 *  paint. When storage is blocked the screen shows on every gated page.
 *  While open, every other body child is inert (the layout marks the skip link and #wrapper on the server; this
 *  element marks the rest), and Tab is kept inside the dialog. "Continue" stores consent and releases the page;
 *  "Take me back" and Escape go back when there is a page to go back to, else home.
 *  Events: adult-gate:shown and adult-gate:accepted, on document and to Microsoft Clarity as adult_gate_shown /
 *  adult_gate_accepted (counts only; never sent to ad platforms). Clarity's own queue is created if its app embed
 *  has not run yet, so an early event waits for it.
 */
if (!customElements.get('adult-gate')) {
	class AdultGate extends HTMLElement {
		connectedCallback() {
			if (this.ready) return;
			this.ready = true;
			this.key = this.dataset.storageKey || 'erobold:adult-consent';
			this.panel = this.querySelector('[role="dialog"]');
			this.acceptButton = this.querySelector('[data-adult-gate-accept]');
			this.declineLink = this.querySelector('[data-adult-gate-decline]');
			this.onKeydown = this.onKeydown.bind(this);

			if (this.acceptButton) this.acceptButton.addEventListener('click', () => this.accept());
			if (this.declineLink) this.declineLink.addEventListener('click', (event) => this.goBack(event));

			if (window.Shopify && window.Shopify.designMode) {
				this.setupEditor();
				return;
			}

			if (this.hasConsent()) {
				this.release(false);
				return;
			}
			this.open();

			// Restored from the back/forward cache after consent was given on another page of this session.
			window.addEventListener('pageshow', (event) => {
				if (event.persisted) this.restored = true;
				if (event.persisted && this.hasConsent()) this.release(false);
			});
		}

		// Theme editor: the screen starts released (layout) and shows while its section is selected, without inert.
		setupEditor() {
			const id = this.dataset.sectionId;
			const show = (event) => {
				if (event.detail.sectionId !== id || !this.isConnected) return;
				this.classList.add('adult-gate--editing');
				this.hidden = false;
			};
			// section:load: a settings change re-renders the section while it is selected.
			document.addEventListener('shopify:section:select', show);
			document.addEventListener('shopify:section:load', show);
			document.addEventListener('shopify:section:deselect', (event) => {
				if (event.detail.sectionId !== id) return;
				this.classList.remove('adult-gate--editing');
			});
		}

		open() {
			this.hidden = false;
			this.lockPage();
			// The script is async and can run while the page is still being parsed: what follows is locked once it is in.
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => {
					if (!document.documentElement.classList.contains('adult-ok')) this.lockPage();
				}, { once: true });
			}
			document.addEventListener('keydown', this.onKeydown);
			if (this.panel) this.panel.focus({ preventScroll: true });
			this.track('adult-gate:shown');
		}

		hasConsent() {
			try {
				return window.sessionStorage.getItem(this.key) === '1';
			} catch (error) {
				return false;
			}
		}

		// Marks every other body child inert, leaving alone ones that are already inert for their own reasons.
		lockPage() {
			const host = this.closest('.shopify-section') || this;
			Array.from(document.body.children).forEach((element) => {
				if (element === host || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'].includes(element.tagName)) return;
				if (element.hasAttribute('inert') && !element.hasAttribute('data-adult-gate-inert')) return;
				element.setAttribute('inert', '');
				element.setAttribute('data-adult-gate-inert', '');
			});
		}

		release(moveFocus) {
			document.removeEventListener('keydown', this.onKeydown);
			document.querySelectorAll('[data-adult-gate-inert]').forEach((element) => {
				element.removeAttribute('inert');
				element.removeAttribute('data-adult-gate-inert');
			});
			document.documentElement.classList.add('adult-ok');
			this.hidden = true;
			if (!moveFocus) return;
			const main = document.getElementById('main-content');
			if (!main) return;
			if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
			main.focus({ preventScroll: true });
		}

		onKeydown(event) {
			if (event.key === 'Escape') {
				event.preventDefault();
				this.goBack();
				return;
			}
			if (event.key !== 'Tab') return;
			const controls = [this.acceptButton, this.declineLink].filter(Boolean);
			if (!controls.length) return;
			const first = controls[0];
			const last = controls[controls.length - 1];
			const active = document.activeElement;
			if (event.shiftKey && (active === first || !this.contains(active) || active === this.panel)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && (active === last || !this.contains(active))) {
				event.preventDefault();
				first.focus();
			}
		}

		accept() {
			try {
				window.sessionStorage.setItem(this.key, '1');
			} catch (error) {
				// Storage blocked (private mode, some in-app browsers): this page opens, the next gated page asks again.
			}
			if (window.Shopify && window.Shopify.designMode) {
				this.classList.remove('adult-gate--editing');
				return;
			}
			this.release(true);
			this.track('adult-gate:accepted');
		}

		// Back when there is a page to go back to, else home; decided before navigating, never by a timer.
		goBack(event) {
			if (event) event.preventDefault();
			if (window.Shopify && window.Shopify.designMode) return;
			const home = (this.declineLink && this.declineLink.href) || '/';
			// A page opened by a link or address (not by Back/Forward, reload or a cache restore) has nothing ahead of it,
			// so any other entry in the tab is behind it, another site's included. A page reached by Back or Forward may
			// have entries ahead: go back only to one of this site's own pages the browser reports, else home.
			const loaded = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
			const fresh = loaded && loaded.type === 'navigate' && !this.restored;
			const canGoBack = fresh ? window.history.length > 1 : Boolean(window.navigation && window.navigation.canGoBack);
			if (canGoBack) window.history.back();
			else window.location.assign(home);
		}

		track(name) {
			document.dispatchEvent(new CustomEvent(name));
			try {
				window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
				window.clarity('event', name.replace(/[^a-z0-9]+/gi, '_'));
			} catch (error) {
				// Analytics must never block the gate.
			}
		}
	}

	customElements.define('adult-gate', AdultGate);
}
